"use client";

import { useCallback, type Dispatch, type SetStateAction } from "react";
import type { RowData, RowSelectionState } from "@tanstack/react-table";
import type { RecordCondition } from "@shared/records/filter-clause";
import { useHydrateOnce } from "@/hooks/use-hydrate-once";
import type { RecordUrlState } from "./state/use-record-url-state";
import {
  ALL_VIEW_ID,
  type RecordObjectDef,
  type RecordSeededView,
  type RecordView,
} from "./types";

type RecordUrlWrite = (next: Partial<RecordUrlState>) => void;

interface UseRecordTableViewsOptions {
  url: RecordUrlState;
  write: RecordUrlWrite;
  isUrlReady: boolean;
  object: Pick<RecordObjectDef<RowData>, "defaultSort" | "seededViews">;
  views: RecordView[];
  defaultViewId: string | undefined;
  initialCondition: RecordCondition | undefined;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
}

export function useRecordTableViews({
  url,
  write,
  isUrlReady,
  object,
  views,
  defaultViewId,
  initialCondition,
  setRowSelection,
}: UseRecordTableViewsOptions): (view: RecordView) => void {
  const { defaultSort, seededViews } = object;
  const writeView = useCallback(
    (view: RecordView) => {
      write({
        viewId: view.id === ALL_VIEW_ID ? undefined : view.id,
        filter: { conjunction: "and", conditions: view.filters },
        sort: view.sort ?? defaultSort,
        columnOrder: view.columnOrder,
        columnHidden: view.columnHidden,
        search: "",
        page: 1,
      });
    },
    [write, defaultSort],
  );

  const applyView = useCallback(
    (view: RecordView) => {
      setRowSelection({});
      writeView(view);
    },
    [setRowSelection, writeView],
  );

  useHydrateOnce(isUrlReady && defaultViewId ? defaultViewId : null, (viewId) =>
    hydrateDefaultView(viewId, url.viewId, views, seededViews, writeView),
  );

  useHydrateOnce(isUrlReady && initialCondition ? initialCondition : null, (condition) =>
    hydrateInitialCondition(condition, url.filter.conditions, write),
  );

  return applyView;
}

function hydrateDefaultView(
  viewId: string,
  activeViewId: string | undefined,
  views: RecordView[],
  seededViews: RecordSeededView[],
  writeView: (view: RecordView) => void,
): void {
  if (activeViewId || window.location.search) return;
  const savedDefault = views.find((view) => view.id === viewId);
  if (savedDefault) {
    writeView(savedDefault);
    return;
  }
  const seededDefault = seededViews.find((view) => view.id === viewId);
  if (seededDefault) {
    writeView({ ...seededDefault, scope: "shared", isOwner: false, isDefault: true });
  }
}

function hydrateInitialCondition(
  condition: RecordCondition,
  conditions: RecordCondition[],
  write: RecordUrlWrite,
): void {
  if (conditions.some((existing) => existing.field === condition.field)) {
    return;
  }
  write({
    filter: {
      conjunction: "and",
      conditions: [...conditions, condition],
    },
    page: 1,
  });
}
